import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useState } from "react";
import { useTerminalActivationCodeQuery } from "Api/Queries/PayPoints/useTerminalActivationCodeQuery";
import { FormSection } from "Components/Shared/FormSection";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Paypoint.Form.Activation;

type Props = {
  payPointPublicID: string;
};

// Shown only for terminals that pair by activation code (SoftPOS app, P300); NEXGO terminals have no code.
// The code stays the same for the life of the TID; a device change only resets the binding in TMS.
export const PaypointActivationSection = ({ payPointPublicID }: Props) => {
  const { t } = useResource();
  const { data } = useTerminalActivationCodeQuery(payPointPublicID);
  const [copied, setCopied] = useState(false);

  const activationCode = data?.activationCode;
  if (!activationCode) {
    return null;
  }

  const handleCopy = async () => {
    await navigator.clipboard.writeText(activationCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <FormSection legend={t(RESOURCES.Legend)}>
      <Stack gap={1}>
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.Code)}
        </Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <Typography
            component="code"
            variant="h6"
            sx={{ letterSpacing: 2, fontFamily: "monospace" }}
          >
            {activationCode}
          </Typography>
          <Tooltip title={copied ? t(RESOURCES.Copied) : t(RESOURCES.Copy)}>
            <IconButton size="small" onClick={handleCopy}>
              <ContentCopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {t(RESOURCES.HelperText)}
        </Typography>
      </Stack>
    </FormSection>
  );
};
